import { ESTADOS_ENVIO, type EstadoEnvio } from '@app/shared';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Edit2, MessageSquareText } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Alert } from '../../../components/ui/alert';
import { Button } from '../../../components/ui/button';
import { useI18n } from '../../../i18n/I18nProvider';
import { getErrorMessage } from '../../../lib/api';
import { cn } from '../../../lib/cn';
import { qk } from '../../../lib/queryKeys';
import { emisionApi } from '../api/emision.api';
import { Resultados, SelloEstado } from '../components/Piezas';

// ciclo-emisor, parte 2: el detalle de una campaña. Los resultados en grande y a
// quién se le mandó, con el link a su charla (para ver qué contestó).

const fechaHora = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleString('es-AR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '-';

export function CampanaDetallePage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const es = useI18n();
  const t = es.campanasEnvio;
  const [estado, setEstado] = useState<EstadoEnvio | undefined>(undefined);

  const campana = useQuery({ queryKey: qk.campanasEnvio.detail(id), queryFn: () => emisionApi.get(id) });
  const envios = useQuery({
    queryKey: qk.campanasEnvio.envios(id, estado),
    queryFn: () => emisionApi.envios(id, estado),
  });
  const c = campana.data;

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/campanas">
              <ArrowLeft size={16} />
              {t.detalle.volver}
            </Link>
          </Button>
          <h1 className="font-titulos text-3xl font-bold text-text-marca">{c?.nombre ?? ''}</h1>
          {c && <SelloEstado estado={c.estado} />}
        </div>
        {c && c.estado !== 'TERMINADA' && (
          <Button variant="outline" asChild>
            <Link to={`/campanas/${id}/editar`}>
              <Edit2 size={16} />
              {t.editar}
            </Link>
          </Button>
        )}
      </div>

      {campana.error && (
        <Alert variant="error">{getErrorMessage(campana.error, es.errores.generico.algoSalioMal)}</Alert>
      )}

      {c && (
        <>
          <p className="text-sm text-text-subtle">
            {t.detalle.ritmoActual
              .replace('{tope}', String(c.topeDiario))
              .replace('{intervalo}', String(c.intervaloMin))
              .replace('{inicio}', String(c.horaInicio))
              .replace('{fin}', String(c.horaFin))}
          </p>
          <Resultados r={c.resultados} />
        </>
      )}

      <section className="rounded-xl border border-border-subtle bg-surface shadow-card">
        <div className="flex items-center justify-between border-b border-border-subtle p-4">
          <h2 className="font-titulos text-xl font-bold text-text-strong">{t.detalle.envios}</h2>
          <label className="flex items-center gap-2 text-sm text-text-subtle">
            {t.detalle.mostrar}
            <select
              value={estado ?? ''}
              onChange={(e) => setEstado((e.target.value || undefined) as EstadoEnvio | undefined)}
              className="h-9 rounded-md border border-marca-neutro bg-surface px-2 text-sm text-marca-gris"
            >
              <option value="">{t.detalle.todos}</option>
              {ESTADOS_ENVIO.map((s) => (
                <option key={s} value={s}>
                  {t.detalle.estadosEnvio[s]}
                </option>
              ))}
            </select>
          </label>
        </div>
        {envios.data && envios.data.length === 0 && (
          <p className="p-4 text-sm text-text-faint">{t.detalle.sinEnvios}</p>
        )}
        {envios.data && envios.data.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-surface-muted text-left text-xs uppercase tracking-wide text-text-subtle">
              <tr>
                <th className="px-4 py-2">{t.detalle.columnas.persona}</th>
                <th className="px-4 py-2">{t.detalle.columnas.estado}</th>
                <th className="px-4 py-2">{t.detalle.columnas.numero}</th>
                <th className="px-4 py-2">{t.detalle.columnas.enviado}</th>
                <th className="px-4 py-2">{t.detalle.columnas.respondio}</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {envios.data.map((e) => (
                <tr key={e.id} className="border-t border-border-subtle">
                  <td className="px-4 py-2 text-text-strong">
                    {e.nombre ?? t.audiencia.sinNombre}{' '}
                    <span className="text-text-faint">· {e.pais ?? '-'}</span>
                  </td>
                  <td className="px-4 py-2" title={e.motivo ?? undefined}>
                    <span className={cn(e.estado === 'NO_ENTREGADO_48H' && 'text-danger')}>
                      {t.detalle.estadosEnvio[e.estado]}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-text-subtle">{e.instancia ?? '-'}</td>
                  <td className="px-4 py-2 whitespace-nowrap text-text-subtle">{fechaHora(e.enviadoEn)}</td>
                  <td className="px-4 py-2">
                    {e.respondio ? <strong className="text-success">{t.detalle.si}</strong> : ''}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {e.leadId && e.enviadoEn && (
                      <Link
                        to={`/bandeja/${e.leadId}`}
                        className="inline-flex items-center gap-1 text-xs font-medium text-marca-primario hover:underline"
                      >
                        <MessageSquareText size={12} />
                        {t.detalle.verCharla}
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
